import storageService from '@/state/services/Storage'
import authService from '@/state/services/Auth'
import apiService from '@/state/services/Api'
import permissionService from '@/state/services/Permission'
import router from '@/router'

const auth = {
  namespaced: true,
  state: {
    tokens: storageService.getTokens() || {access: null, refresh: null},
    currentUser: storageService.getCurrentUser(),
    isTokenRefreshing: false,
    isRetrievingCompanyProfile: false,
  },
  getters: {
    isAuthenticated(state) {
      return !!state.currentUser && !!state.tokens.access
    },
    currentUser(state) {
      return state.currentUser
    },
    currentUserId(state) {
      return state.currentUser?.user_uuid || 0
    },
    isStaff(state) {
      return state.currentUser?.is_admin
    },
    isImpersonating(state) {
      return !!state.currentUser?.original_user
    },
    isUser(state, getters) {
      return !getters['isStaff']
    },
    userPermissions(state) {
      return state.currentUser?.profile?.permissions || []
    },
    hasInactiveProfile(state) {
      return (
        state.currentUser?.companies &&
        state.currentUser?.companies.length > 0 &&
        !state.currentUser?.profile
      )
    },
    companyProfiles(state) {
      return state.currentUser?.companies || []
    },
    isRefreshing(state) {
      return state.isTokenRefreshing
    },
    activeProfile(state) {
      return state.currentUser?.profile || {}
    },
    activeCompany(state, getters) {
      return getters['activeProfile'].company_id
    },
    getAccessToken(state) {
      return state.tokens.access
    },
    isPermitted(state, getters) {
      return rule => {
        if (!rule || typeof rule !== 'string' || getters['isStaff']) {
          return true
        }
        const i = rule.indexOf('_')
        const action = rule.slice(0, i)
        const resource = rule.slice(i + 1)
        return permissionService.can(action, resource)
      }
    },
    services(state) {
      return state.currentUser?.services || []
    },
    isChangingCompanyProfile(state) {
      return state.isRetrievingCompanyProfile
    },
  },
  actions: {
    async logIn({dispatch}, {email, password}) {
      await dispatch('getTokens', {email, password})
      await dispatch('getCurrentUser')
    },

    getTokens({commit}, {email, password}) {
      return authService.post(`token/`, {email, password}).then(tokens => {
        commit('SET_TOKENS', tokens)
        return tokens
      })
    },

    simulateTablet() {
      const email = 'admin@admin.admin'
      const password = 'adminpass'
      const android_id = 'b2163efc-249b-4bd3-958e-7ecbea1158a7'
      return authService.post(`token/`, {email, password, android_id}).then(tokens => {
        return tokens
      })
    },

    getCurrentUser({state, commit}) {
      return authService
        .get(`current/`, {headers: {Authorization: `Bearer ${state.tokens.access}`}})
        .then(user => {
          commit('SET_CURRENT_USER', user)
          return user
        })
    },

    getProfileForUser({state, commit, getters}, id) {
      commit('START_PROFILE_CHANGE')
      return apiService.get(`auth/current/?company=${id}`).then(user => {
        commit('SET_CURRENT_USER', user)
        commit('FINISH_PROFILE_CHANGE')
        return user
      })
    },

    async changeDefaultCompany({state}, id) {
      let profileId = state.currentUser.companies.find(item => item.id === id).profile_id
      return await apiService.patch(`/users/${state.currentUser.user_uuid}/profiles/${profileId}`, {
        is_default: true,
      })
    },

    refreshToken({state, commit}) {
      return authService.post(`token/refresh/`, {refresh: state.tokens.refresh}).then(token => {
        commit('SET_TOKENS', token)
        return token
      })
    },

    tokenExpired({dispatch, getters}) {
      if (getters.isAuthenticated) {
        dispatch('logOut')
        router.replace({name: 'login', redirect: router.currentRoute.fullPath})
        dispatch(
          `app/notification`,
          {group: 'auth', text: 'token_expired', type: 'error'},
          {root: true}
        )
      }
    },

    async impersonate({dispatch, state, commit}, uuid) {
      await dispatch('getImpersonationTokens', uuid)
      await dispatch('getCurrentUser')
    },

    getImpersonationTokens({state, commit}, uuid) {
      return apiService.post('auth/impersonate/', {user: uuid}).then(tokens => {
        commit('SET_TOKENS', tokens)
        return tokens
      })
    },

    async stopImpersonate({dispatch}) {
      await dispatch('deleteImpersonationTokens')
      await dispatch('getCurrentUser')
    },

    deleteImpersonationTokens({commit}) {
      return apiService.post('auth/impersonate/stop').then(tokens => {
        commit('SET_TOKENS', tokens)
        return tokens
      })
    },

    logOut({commit}) {
      commit('PURGE_AUTH')
    },

    startRefreshing({commit}) {
      commit('START_REFRESH')
    },

    finishRefreshing({commit}) {
      commit('FINISH_REFRESH')
    },
  },
  mutations: {
    SET_TOKENS(state, tokens) {
      state.tokens = Object.assign({}, state.tokens, tokens)
      storageService.saveTokens(tokens)
    },
    SET_CURRENT_USER(state, user) {
      // Demo Subscriptions/Services
      user.subscriptions = subscriptions
      user.services = services

      state.currentUser = user
      storageService.saveCurrentUser(user)
      // plugins/boot.js
    },
    UPDATE_CURRENT_USER(state, data) {
      const [attr] = Object.keys(data)
      state.currentUser[attr] = data[attr]
      // state.currentUser = Object.assign({}, state.currentUser, { attr: data[attr]})
      storageService.saveCurrentUser(state.currentUser)
    },
    PURGE_AUTH(state) {
      state.tokens = {access: null, refresh: null}
      state.currentUser = null
      storageService.purgeTokens();
      storageService.purgeCurrentUser();
      // plugins/boot.js
    },
    START_REFRESH(state) {
      state.isTokenRefreshing = true
    },
    FINISH_REFRESH(state) {
      state.isTokenRefreshing = false
    },
    START_PROFILE_CHANGE(state) {
      state.isRetrievingCompanyProfile = true
    },
    FINISH_PROFILE_CHANGE(state) {
      state.isRetrievingCompanyProfile = false
    },
  },
}

export default auth

const subscriptions = [
  {
    name: 'KEY_HANDLING',
    stock: 10,
  },
  {
    name: 'SAMPLE_COLLECTOR',
    stock: 2,
  },
]

const services = [
  {
    id: 1,
    name: 'core',
    editable: false,
  },
  {
    id: 2,
    name: 'sonico',
    editable: true,
    data: {
      profiles: [
        {
          id: '01',
          name: 'Customer',
        },
        {
          id: '02',
          name: 'Admin Customer',
        },
        {
          id: '03',
          name: 'GWF Technician',
        },
        {
          id: '04',
          name: 'ENG/Production',
        },
      ],
    },
  },
  {
    id: 3,
    name: 'mex',
    editable: true,
  },
]
